import type {
  BookSummary, BookImportResponse, ImportBookRequest, UpdateBookRequest,
  QuizSession, QuestionWithContext, StartConversationResponse,
  AnswerResult, Stats, SubscriptionResponse, LearningSettings, UpdateLearningSettingsRequest,
  SearchResult, AskResponse
} from '../types/models'

let authToken: string | null = null

export function setAuthToken(token: string | null) {
  authToken = token
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly code: 'UNAUTHORIZED' | 'PAYMENT_REQUIRED' | 'NOT_FOUND' | 'SERVER_ERROR' | 'NETWORK_ERROR'
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

const BASE = import.meta.env.GCP_API_URL as string

const REQUEST_TIMEOUT_MS = 30_000
// Imports and exports move files, so they get a longer deadline.
const TRANSFER_TIMEOUT_MS = 180_000

function withDeadline(options: RequestInit, timeoutMs: number): RequestInit {
  const deadline = AbortSignal.timeout(timeoutMs)
  const signal = options.signal ? AbortSignal.any([options.signal, deadline]) : deadline
  return { ...options, signal }
}

async function apiUpload<T>(path: string, body: FormData): Promise<T> {
  const headers: Record<string, string> = {}
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`
  // Never set Content-Type here: the browser must add the multipart boundary.

  let response: Response
  try {
    response = await fetch(`${BASE}${path}`, withDeadline({ method: 'POST', headers, body }, TRANSFER_TIMEOUT_MS))
  } catch {
    throw new ApiError('Network error — check your connection', 'NETWORK_ERROR')
  }

  if (response.ok) return response.json() as Promise<T>
  if (response.status === 401) throw new ApiError('Unauthorized', 'UNAUTHORIZED')
  if (response.status === 402) throw new ApiError('Subscription required', 'PAYMENT_REQUIRED')
  if (response.status === 404) throw new ApiError('Not found', 'NOT_FOUND')
  if (response.status === 413) throw new ApiError('File too large (max 15 MB)', 'SERVER_ERROR')

  let message = `Server error (${response.status})`
  try {
    const b = await response.json()
    if (b.message) message = b.message
  } catch { /* ignore */ }
  throw new ApiError(message, 'SERVER_ERROR')
}

async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`

  let response: Response
  try {
    response = await fetch(`${BASE}${path}`, withDeadline({ ...options, headers }, REQUEST_TIMEOUT_MS))
  } catch {
    throw new ApiError('Network error — check your connection', 'NETWORK_ERROR')
  }

  if (response.ok) {
    if (response.status === 204) return undefined as T
    return response.json() as Promise<T>
  }

  if (response.status === 401) throw new ApiError('Unauthorized', 'UNAUTHORIZED')
  if (response.status === 402) throw new ApiError('Subscription required', 'PAYMENT_REQUIRED')
  if (response.status === 404) throw new ApiError('Not found', 'NOT_FOUND')

  let message = `Server error (${response.status})`
  try {
    const body = await response.json()
    if (body.message) message = body.message
  } catch { /* ignore */ }
  throw new ApiError(message, 'SERVER_ERROR')
}

export const getBooks = () => apiFetch<BookSummary[]>('/books')
export const getBook = (id: string) => apiFetch<BookSummary>(`/books/${id}`)
export const searchHighlights = (query: string, topK = 5) =>
  apiFetch<SearchResult[]>('/search', { method: 'POST', body: JSON.stringify({ query, topK }) })
export const askLibrary = (question: string, topK = 5) =>
  apiFetch<AskResponse>('/search/ask', { method: 'POST', body: JSON.stringify({ question, topK }) })
export const importBook = (req: ImportBookRequest) => apiFetch<BookImportResponse>('/books', { method: 'POST', body: JSON.stringify(req) })
export const updateBook = (id: string, req: UpdateBookRequest) => apiFetch<BookSummary>(`/books/${id}`, { method: 'PATCH', body: JSON.stringify(req) })
export const deleteBook = (id: string) => apiFetch<void>(`/books/${id}`, { method: 'DELETE' })
export const retryGeneration = (id: string) => apiFetch<void>(`/books/${id}/retry-generation`, { method: 'POST' })
export const importPdf = (file: File, title: string, author: string) => {
  const fd = new FormData()
  fd.append('file', file)
  fd.append('title', title)
  fd.append('author', author)
  fd.append('generate_questions', 'true')
  fd.append('source', 'web_pdf')
  return apiUpload<BookImportResponse>('/books/import-pdf', fd)
}
export const importKoboExport = (file: File) => {
  const fd = new FormData()
  fd.append('file', file)
  fd.append('generate_questions', 'true')
  return apiUpload<BookImportResponse[]>('/books/import-kobo', fd)
}
export const importKindleClippings = (file: File) => {
  const fd = new FormData()
  fd.append('file', file)
  fd.append('generate_questions', 'true')
  return apiUpload<BookImportResponse[]>('/books/import-kindle-clippings', fd)
}
export const importReadwise = (file: File) => {
  const fd = new FormData()
  fd.append('file', file)
  fd.append('generate_questions', 'true')
  return apiUpload<BookImportResponse[]>('/books/import-readwise', fd)
}
// Browser IANA zone, not the backend's UTC default, so day buckets follow the user's calendar.
const browserZoneId = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
export const generateSession = async (
  maxQuestions = 10,
  includeNewQuestions = true,
  focusOnWeakTopics = true,
): Promise<QuizSession> => {
  const zoneId = browserZoneId()
  const raw = await apiFetch<{ sessionId: string; questions: QuestionWithContext[]; reviewDate: string; stats?: Stats | null }>(
    '/quiz/session',
    { method: 'POST', body: JSON.stringify({ maxQuestions, includeNewQuestions, focusOnWeakTopics, zoneId }) },
  )
  return { sessionId: raw.sessionId, concepts: raw.questions, reviewDate: raw.reviewDate, stats: raw.stats ?? undefined }
}
export const startConversation = (questionId: string) =>
  apiFetch<StartConversationResponse>('/quiz/conversation/start', { method: 'POST', body: JSON.stringify({ questionId }) })
export const forfeitConversation = (sessionId: string) =>
  apiFetch<AnswerResult>(`/quiz/conversation/${sessionId}/forfeit`, { method: 'POST' })
export const getStats = () =>
  apiFetch<Stats>(`/quiz/stats?zoneId=${encodeURIComponent(browserZoneId())}`)
export const submitFeedback = (req: { traceId: string; rating: 'UP' | 'DOWN'; turn?: number }) =>
  apiFetch<void>('/quiz/conversation/feedback', { method: 'POST', body: JSON.stringify(req) })

export const getSubscription = () => apiFetch<SubscriptionResponse>('/billing/subscription')

export const deleteAccount = () => apiFetch<void>('/account', { method: 'DELETE' })

/** Raw fetch, not apiFetch: the response is a binary ZIP, not JSON. */
export async function exportData(): Promise<{ blob: Blob; filename: string }> {
  const headers: Record<string, string> = {}
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`

  let response: Response
  try {
    response = await fetch(`${BASE}/account/export`, withDeadline({ headers }, TRANSFER_TIMEOUT_MS))
  } catch {
    throw new ApiError('Network error — check your connection', 'NETWORK_ERROR')
  }

  if (!response.ok) {
    if (response.status === 401) throw new ApiError('Unauthorized', 'UNAUTHORIZED')
    if (response.status === 402) throw new ApiError('Subscription required', 'PAYMENT_REQUIRED')
    throw new ApiError(`Server error (${response.status})`, 'SERVER_ERROR')
  }

  const blob = await response.blob()
  const disposition = response.headers.get('Content-Disposition') ?? ''
  const filename = /filename="?([^"]+)"?/.exec(disposition)?.[1] ?? 'recall-export.zip'
  return { blob, filename }
}

export const getLearningSettings = () => apiFetch<LearningSettings>('/settings')
export const updateLearningSettings = (req: UpdateLearningSettingsRequest) =>
  apiFetch<LearningSettings>('/settings', { method: 'PUT', body: JSON.stringify(req) })
